import { Grid, MultiSelect, SegmentedControl, Select, TagsInput, Text, TextInput } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import prettifiedLabel from '../../constants/labelPrettifier';
import useFetch from '../../helpers/useFetch';
import DatePicker from '../DatePicker';
import ErrorMessage from '../ErrorMessage';
import ScheduleSelector from '../ScheduleSelector';
import Spinner from '../Spinner';

const entities = [
  'recommenders',
  'bannerBlocks',
  'banners',
  'campaigns',
  'emails',
  'pushNotifications',
  'textMessages',
  'viberMessages',
  'segments',
  'facebookAds',
  'catalogTriggers',
  'whatsappMessages',
  'surveys',
  'webhooks',
  'npsSurveys',
  'chatSessions',
];

const SCOPES_WITHOUT_ELEMENTS = ['profile', 'event', 'chatSessions'];

const SCOPE_LABELS = { chatSessions: 'Chat conversations' };

const ReportsForm = ({ report, handleChange }) => {
  const [userRoles, setUserRoles] = useState();
  const [elements, setElements] = useState();
  const { domain } = useSelector((state) => state.domain);
  const [loadError, setLoadError] = useState();
  const fetch = useFetch();

  useEffect(() => {
    fetch(`/api/admin/accounts/${domain.accountId}/userRoles`)
      .then(({ userRoles }) => {
        setUserRoles(userRoles);
      })
      .catch(setLoadError);
  }, [domain.accountId, fetch]);

  const fetchUrl = `/api/admin/accounts/${domain.accountId}/domains/${domain.id}`;

  useEffect(() => {
    if (report?.scope === 'recommenders' || report?.scope === 'bannerBlocks') {
      fetch(`${fetchUrl}/pages`)
        .then((response) => {
          setElements(
            response.pages
              .map((page) => page[report.scope])
              .flat()
              .map((e) => ({ label: e.name, value: e.id.toString() })),
          );
        })
        .catch(setLoadError);
    } else if (report?.scope === 'npsSurveys') {
      fetch(`${fetchUrl}/nps-surveys`)
        .then((response) => {
          setElements(response.npsSurveys.map((e) => ({ label: e.name, value: e.id.toString() })));
        })
        .catch(setLoadError);
    } else if (SCOPES_WITHOUT_ELEMENTS.includes(report?.scope)) {
      setElements([]);
    } else if (report?.scope && entities.includes(report?.scope)) {
      fetch(`${fetchUrl}/${report.scope}`)
        .then((response) => {
          setElements(response[report.scope].map((e) => ({ label: e.name, value: e.id.toString() })));
        })
        .catch(setLoadError);
    } else {
      setElements([]);
    }
  }, [fetch, fetchUrl, report?.scope]);

  const scopeHasNoElements = SCOPES_WITHOUT_ELEMENTS.includes(report?.scope);
  const staleSpecificSelection = report?.scope === 'chatSessions' && report?.selection === 'specific';

  useEffect(() => {
    if (staleSpecificSelection) handleChange({ target: { name: 'selection', value: 'all' } });
  }, [staleSpecificSelection, handleChange]);

  if (report === null) {
    return (
      <ErrorMessage message="There was an error loading the report. Please contact the support for resolving the issue." />
    );
  }

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (report === undefined || userRoles === undefined || elements === undefined) {
    return <Spinner />;
  }

  const emails = [...new Set(userRoles.map((role) => role.user.email))];

  const nameElement = (
    <Grid align="center" mb="sm" data-tour="reportName">
      <Grid.Col span={{ base: 12, sm: 2 }}>
        <Text fw={500}>Name</Text>
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 8 }}>
        <TextInput name="name" value={report.name} placeholder="New Report" onChange={(event) => handleChange(event)} />
      </Grid.Col>
    </Grid>
  );

  const typeElement = (
    <Grid align="center" mb="sm" data-tour="reportType">
      <Grid.Col span={{ base: 12, sm: 2 }}>
        <Text fw={500}>Type</Text>
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 8 }}>
        <SegmentedControl
          disabled={!!report.id}
          value={report.type ?? ''}
          onChange={(type) => handleChange({ target: { name: 'type', value: type } })}
          data={[
            { label: 'One Time', value: 'oneTime' },
            { label: 'Periodic', value: 'periodic' },
          ]}
        />
      </Grid.Col>
    </Grid>
  );

  const periodElement = (
    <Grid align="center" mb="sm" data-tour="reportPeriod">
      <Grid.Col span={{ base: 12, sm: 2 }}></Grid.Col>
      <Grid.Col span={{ base: 12, sm: 8 }}>
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <DatePicker
              prepend="from"
              disabled={report.id}
              size="sm"
              showTimeSelect
              placeholder="Any date"
              value={report.periodStart || ''}
              onChange={(value) => handleChange({ target: { name: 'periodStart', value } })}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <DatePicker
              prepend="to"
              disabled={report.id}
              size="sm"
              showTimeSelect
              placeholder="Any date"
              value={report.periodEnd || ''}
              onChange={(value) => handleChange({ target: { name: 'periodEnd', value } })}
            />
          </Grid.Col>
        </Grid>
        <Text size="sm" c="dimmed" mt="xs">
          Note that report date/times selected here are in <strong>your local time</strong>.
        </Text>
      </Grid.Col>
    </Grid>
  );

  const scheduleElement = (
    <Grid align="center" mb="sm">
      <Grid.Col span={{ base: 12, sm: 2 }}>
        <Text fw={500}>Schedule</Text>
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 8 }}>
        <ScheduleSelector
          schedule={report.schedule}
          updateSchedule={(value) => handleChange({ target: { name: 'schedule', value } })}
        />
        <Text size="sm" c="dimmed" mt="xs">
          This controls the interval for automatic execution for the report: <br />- If we want to receive report every
          week on monday at 9:00AM - <strong>Every</strong> to 1, <strong>Type</strong> to week, <strong>Day</strong> to
          M, <strong>Time</strong> to 9:00AM <br />- If we want to receive report every two months on 3rd day of the
          month at 9:00AM - <strong>Every</strong> to 2, <strong>Type</strong> to month, <strong>Day</strong> to 3,{' '}
          <strong>Time</strong> to 9:00AM
        </Text>
      </Grid.Col>
    </Grid>
  );

  const entityOptions = entities.map((e) => ({ label: SCOPE_LABELS[e] || prettifiedLabel(e), value: e }));

  const scopeElement = (
    <Grid align="center" mb="sm" data-tour="reportScope">
      <Grid.Col span={{ base: 12, sm: 2 }}>
        <Text fw={500}>Scope</Text>
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 8 }} data-tour="reportScopeSelect">
        <Select
          disabled={report.id}
          searchable
          value={report.scope}
          data={entityOptions}
          onChange={(value) => handleChange({ target: { name: 'scope', value } })}
          placeholder="Select scope of the report"
        />
      </Grid.Col>
    </Grid>
  );

  const selectionElement = (
    <Grid align="center" mb="sm" data-tour="reportSelection">
      <Grid.Col span={{ base: 12, sm: 2 }}>
        <Text fw={500}>Selection</Text>
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 8 }}>
        <SegmentedControl
          disabled={!!report.id && report.type === 'oneTime'}
          value={report.selection ?? ''}
          onChange={(selection) => handleChange({ target: { name: 'selection', value: selection } })}
          data={[
            { label: 'All', value: 'all' },
            { label: 'Specific', value: 'specific' },
          ]}
        />
      </Grid.Col>
    </Grid>
  );

  const elementsElement = (
    <Grid align="center" mb="sm">
      <Grid.Col span={{ base: 12, sm: 2 }}></Grid.Col>
      <Grid.Col span={{ base: 12, sm: 8 }}>
        <MultiSelect
          disabled={report.id && report.type === 'oneTime'}
          value={report.elements.map((e) => e.toString())}
          data={elements}
          onChange={(value) => handleChange({ target: { name: 'elements', value } })}
          placeholder="Select elements of the report"
        />
      </Grid.Col>
    </Grid>
  );

  const recipientsElement = (
    <Grid align="center" mb="sm" data-tour="reportRecipients">
      <Grid.Col span={{ base: 12, sm: 2 }}>
        <Text fw={500}>Recipients</Text>
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 8 }}>
        <TagsInput
          disabled={report.id && report.type === 'oneTime'}
          clearable
          value={report.recipients}
          data={emails}
          onChange={(value) => handleChange({ target: { name: 'recipients', value } })}
          placeholder="Select recipients of the report"
        />
      </Grid.Col>
    </Grid>
  );

  return (
    <form>
      {nameElement}
      {typeElement}
      {report.type === 'periodic'
        ? scheduleElement
        : !report.id || report.periodStart || report.periodEnd
          ? periodElement
          : null}
      {scopeElement}
      {scopeHasNoElements ? null : selectionElement}
      {!scopeHasNoElements && report.selection === 'specific' ? elementsElement : null}
      {recipientsElement}
    </form>
  );
};

export default ReportsForm;
